import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import { TAG_COLORS } from "../components/ForumPostCard";
import { X } from "lucide-react";

const SUGGESTED_TAGS = ["wins","question","advice","resource","rant","microsaas","freelance","hackathon","workshops","notiontemplates"];

export default function CreateForumPost() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ title: "", body: "", tags: [] });
  const [customTag, setCustomTag] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const addTag = (t) => {
    const clean = t.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
    if (!clean || form.tags.includes(clean)) return;
    setForm((f) => ({ ...f, tags: [...f.tags, clean] }));
  };

  const removeTag = (t) => setForm((f) => ({ ...f, tags: f.tags.filter((x) => x !== t) }));

  const toggleSuggested = (t) => {
    if (form.tags.includes(t)) removeTag(t);
    else addTag(t);
  };

  const handleCustomTagKey = (e) => {
    if ((e.key === "Enter" || e.key === ",") && customTag.trim()) {
      e.preventDefault();
      addTag(customTag);
      setCustomTag("");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { nav("/login"); return; }
    if (!form.title.trim() || !form.body.trim()) { setErr("Title and body are required."); return; }
    setErr(""); setBusy(true);
    try {
      const { data, error } = await supabase.from("forum_posts").insert({
        user_id:   user.id,
        title:     form.title.trim(),
        body:      form.body.trim(),
        tags:      form.tags,
        upvotes:   0,
        reply_count: 0,
      }).select().single();
      if (error) throw error;
      nav(`/forum/${data.id}`);
    } catch (e) {
      setErr(e.message || "Failed to post.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
      <div className="font-hand text-marker-red text-3xl rotate-[-1deg] mb-1">— say something real —</div>
      <h1 className="font-display font-extrabold text-5xl uppercase tracking-tight mb-6">New Post</h1>

      <form onSubmit={submit} className="space-y-5" data-testid="create-forum-post-form">
        <div className="brutal-card p-6 space-y-4">
          <label className="block">
            <div className="font-mono text-xs uppercase tracking-widest mb-1">Title</div>
            <input
              className="brutal-input" required
              placeholder="Be specific. 'How I got 5 clients' > 'My freelance journey'"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="forum-title-input"
            />
          </label>

          <label className="block">
            <div className="font-mono text-xs uppercase tracking-widest mb-1">Body</div>
            <div className="font-mono text-[11px] text-ink/50 mb-1">Supports **bold** and paragraphs.</div>
            <textarea
              className="brutal-input min-h-[260px] text-sm leading-relaxed resize-y"
              required
              placeholder="Share the full story — wins, losses, numbers, lessons. The community wants receipts."
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              data-testid="forum-body-input"
            />
          </label>

          <div>
            <div className="font-mono text-xs uppercase tracking-widest mb-2">Tags</div>
            <div className="font-mono text-[11px] text-ink/50 mb-2">Pick from suggestions or type your own below.</div>

            {/* Suggested tags */}
            <div className="flex flex-wrap gap-2 mb-3">
              {SUGGESTED_TAGS.map((t) => {
                const style = TAG_COLORS[t] || { bg: "#FFD600", color: "#121212" };
                const selected = form.tags.includes(t);
                return (
                  <button key={t} type="button" onClick={() => toggleSuggested(t)} className="tag-pill"
                    style={{
                      background: selected ? style.bg : "#fff",
                      color:      selected ? style.color : "#121212",
                      border:     `2px solid ${selected ? style.bg : "#121212"}`,
                    }}
                    data-testid={`tag-toggle-${t}`}>
                    #{t}
                  </button>
                );
              })}
            </div>

            {/* Custom tag input */}
            <div className="flex gap-2 mb-3">
              <input
                className="brutal-input flex-1"
                placeholder="Type a custom tag and press Enter…"
                value={customTag}
                onChange={(e) => setCustomTag(e.target.value)}
                onKeyDown={handleCustomTagKey}
                data-testid="custom-tag-input"
              />
              <button
                type="button"
                onClick={() => { if (customTag.trim()) { addTag(customTag); setCustomTag(""); } }}
                className="brutal-btn brutal-btn-yellow text-xs"
              >
                Add
              </button>
            </div>

            {/* Selected tags display */}
            {form.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {form.tags.map((t) => {
                  const style = TAG_COLORS[t] || { bg: "#121212", color: "#fff" };
                  return (
                    <span key={t} className="tag-pill flex items-center gap-1"
                      style={{ background: style.bg, color: style.color }}>
                      #{t}
                      <button type="button" onClick={() => removeTag(t)}
                        className="hover:opacity-70">
                        <X size={12} />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {err && (
            <div className="border-2 border-marker-red bg-marker-red/10 p-3 font-mono text-sm">{err}</div>
          )}

          <button type="submit" disabled={busy} className="brutal-btn brutal-btn-yellow w-full" data-testid="forum-submit-btn">
            {busy ? "Posting…" : "Post to forum"}
          </button>
        </div>
      </form>
    </main>
  );
}
